import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, RefreshCw, Download, LogOut, Mail, Phone, AlertCircle, Inbox, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, ADMIN_TOKEN_KEY } from "@/lib/api";
import { SIM_LABELS } from "@/lib/format";
import { TrafficDashboard } from "@/components/TrafficDashboard";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const TYPE_META = {
  reception_simulation: { label: "Réception", color: "bg-[#4A6277]/12 text-[#4A6277]" },
  contact_expert: { label: "Rappel", color: "bg-[#C84C2C]/12 text-[#C84C2C]" },
};

function PinScreen({ onSuccess }) {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/admin/verify-pin", { password });
      localStorage.setItem(ADMIN_TOKEN_KEY, data.session);
      onSuccess();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Mot de passe incorrect.";
      setError(msg);
      setPassword("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen px-6 pt-6 pb-12" data-testid="admin-pin-screen">
      <button
        onClick={() => navigate("/")}
        data-testid="admin-back-home"
        className="h-9 w-9 -ml-2 mb-8 rounded-full flex items-center justify-center text-[#8C7A70] hover:bg-[#F5EFE6]"
      >
        <ArrowLeft strokeWidth={1.5} className="h-5 w-5" />
      </button>

      <div className="flex-1 flex flex-col justify-center animate-fade-up">
        <div className="h-14 w-14 rounded-full bg-[#1C1613] flex items-center justify-center mb-6">
          <Lock strokeWidth={1.5} className="h-6 w-6 text-[#FFFFFF]" />
        </div>
        <h1 className="font-serif text-3xl font-semibold text-[#231C18]">Accès protégé</h1>
        <p className="mt-2 text-sm text-[#8C7A70]">Entrez votre mot de passe.</p>

        <input
          data-testid="admin-pin-input"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && password.length > 0 && submit()}
          placeholder="Mot de passe"
          className="mt-8 h-[54px] w-full bg-transparent border border-[#E6DDD4] rounded-xl px-4 text-base font-medium text-[#231C18] outline-none focus:border-[#C84C2C] focus:ring-1 focus:ring-[#C84C2C]"
        />
        {error && (
          <p className="mt-3 text-sm text-[#C62828] flex items-center gap-1.5">
            <AlertCircle strokeWidth={1.5} className="h-4 w-4" /> {error}
          </p>
        )}

        <button
          onClick={submit}
          disabled={password.length === 0 || loading}
          data-testid="admin-unlock-btn"
          className="mt-6 h-[56px] w-full bg-[#C84C2C] text-white rounded-full font-semibold shadow-[0_8px_20px_-6px_rgba(200,76,44,0.55)] transition-transform active:scale-[0.98] hover:bg-[#B23E20] disabled:opacity-40"
        >
          {loading ? "Vérification…" : "Déverrouiller"}
        </button>
      </div>
    </div>
  );
}

function DeleteLeadButton({ lead, onDelete }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button
          data-testid={`admin-delete-lead-${lead.id}`}
          aria-label="Supprimer le lead"
          className="h-8 w-8 rounded-full flex items-center justify-center text-[#8C7A70] transition-colors hover:bg-[#C62828]/10 hover:text-[#C62828]"
        >
          <Trash2 strokeWidth={1.5} className="h-4 w-4" />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="admin-delete-dialog" className="w-[calc(100%-2rem)] max-w-[400px] rounded-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-serif">Supprimer ce lead ?</AlertDialogTitle>
          <AlertDialogDescription>
            {lead.prenom} {lead.nom} sera définitivement supprimé de l'espace admin. Le contact reste dans Brevo.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="admin-delete-cancel" className="rounded-full">Annuler</AlertDialogCancel>
          <AlertDialogAction
            data-testid="admin-delete-confirm"
            onClick={() => onDelete(lead)}
            className="rounded-full bg-[#C62828] hover:bg-[#A61F1F]"
          >
            Supprimer
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function LeadCard({ lead: l, onDelete }) {
  const meta = TYPE_META[l.type] || { label: l.type, color: "bg-[#F5EFE6] text-[#8C7A70]" };
  return (
    <div data-testid="admin-lead-card" className="bg-[#FFFFFF] border border-[#E6DDD4] rounded-2xl p-4 animate-fade-up">
      <div className="flex items-start justify-between gap-3">
        <p className="font-semibold text-[#231C18]">
          {l.prenom} {l.nom}
        </p>
        <div className="flex items-center gap-1 shrink-0">
          <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${meta.color}`}>
            {meta.label}
          </span>
          <DeleteLeadButton lead={l} onDelete={onDelete} />
        </div>
      </div>
      <div className="mt-2 flex flex-col gap-1.5">
        <a href={`mailto:${l.email}`} className="inline-flex items-center gap-2 text-sm text-[#4A6277]">
          <Mail strokeWidth={1.5} className="h-3.5 w-3.5" /> {l.email}
        </a>
        {l.telephone && (
          <a href={`tel:${l.telephone}`} className="inline-flex items-center gap-2 text-sm text-[#4A6277]">
            <Phone strokeWidth={1.5} className="h-3.5 w-3.5" /> {l.telephone}
          </a>
        )}
      </div>
      <div className="mt-3 pt-3 border-t border-[#E6DDD4] text-xs text-[#8C7A70] space-y-1">
        <p className="font-medium text-[#231C18]">{SIM_LABELS[l.simulateur] || l.simulateur}</p>
        <p>{new Date(l.created_at).toLocaleString("fr-FR")}</p>
        {l.utm_source && (
          <p data-testid="admin-lead-source">
            Source : <span className="font-medium text-[#231C18]">{l.utm_source}{l.utm_content ? ` / ${l.utm_content}` : ""}</span>
            {l.device ? ` · ${l.device}` : ""}
            {l.gclid ? " · gclid" : ""}
            {l.fbclid ? " · fbclid" : ""}
          </p>
        )}
        {l.resume && <p className="italic">{l.resume}</p>}
      </div>
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(!!localStorage.getItem(ADMIN_TOKEN_KEY));
  const [leads, setLeads] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [filter, setFilter] = useState("all");
  const [tab, setTab] = useState("leads");

  const fetchLeads = useCallback(async () => {
    const token = localStorage.getItem(ADMIN_TOKEN_KEY);
    if (!token) {
      setAuthed(false);
      return;
    }
    setStatus("loading");
    try {
      const { data } = await api.get("/leads", { headers: { "X-Admin-Session": token } });
      setLeads(data);
      setStatus("ok");
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        setAuthed(false);
      } else {
        setStatus("error");
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (authed) fetchLeads();
  }, [authed, fetchLeads]);

  const counts = useMemo(
    () => ({
      all: leads.length,
      reception_simulation: leads.filter((l) => l.type === "reception_simulation").length,
      contact_expert: leads.filter((l) => l.type === "contact_expert").length,
    }),
    [leads]
  );

  const filtered = useMemo(
    () => (filter === "all" ? leads : leads.filter((l) => l.type === filter)),
    [leads, filter]
  );

  const deleteLead = async (lead) => {
    try {
      await api.delete(`/leads/${lead.id}`, { headers: { "X-Admin-Session": localStorage.getItem(ADMIN_TOKEN_KEY) } });
      setLeads((prev) => prev.filter((l) => l.id !== lead.id));
      toast.success("Lead supprimé.");
    } catch (e) {
      if (e?.response?.status === 401) logout();
      else toast.error("La suppression a échoué. Réessayez.");
    }
  };

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setAuthed(false);
    setLeads([]);
  };

  const exportCsv = () => {
    const header = "Type;Prénom;Nom;Email;Téléphone;Simulateur;Résumé;Date;utm_source;utm_content;gclid;fbclid;device";
    const rows = filtered.map((l) => {
      const type = TYPE_META[l.type]?.label || l.type;
      const sim = SIM_LABELS[l.simulateur] || l.simulateur || "";
      const date = new Date(l.created_at).toLocaleString("fr-FR");
      const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
      return [type, l.prenom, l.nom, l.email, l.telephone, sim, l.resume, date, l.utm_source, l.utm_content, l.gclid, l.fbclid, l.device].map(esc).join(";");
    });
    const csv = "\uFEFF" + [header, ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads_assertimmo_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export généré.");
  };

  if (!authed) return <PinScreen onSuccess={() => setAuthed(true)} />;

  const chips = [
    { key: "all", label: "Tous", count: counts.all },
    { key: "reception_simulation", label: "Réceptions", count: counts.reception_simulation },
    { key: "contact_expert", label: "Rappels", count: counts.contact_expert },
  ];

  return (
    <div className="flex flex-col min-h-screen" data-testid="admin-dashboard">
      <div className="sticky top-0 z-30 bg-[#FAF6F0]/90 backdrop-blur-md px-6 pt-6 pb-4 border-b border-[#E6DDD4]">
        <div className="flex items-center justify-between mb-4">
          <div className="flex bg-[#F4EFE6] rounded-full p-1" data-testid="admin-tabs">
            {[["leads", "Leads"], ["trafic", "Trafic"]].map(([k, label]) => (
              <button
                key={k}
                data-testid={`admin-tab-${k}`}
                onClick={() => setTab(k)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                  tab === k ? "bg-[#1C1613] text-white" : "text-[#8C7A70]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            {tab === "leads" && (<>
            <button onClick={fetchLeads} data-testid="admin-refresh-btn" className="h-9 w-9 rounded-full flex items-center justify-center text-[#8C7A70] hover:bg-[#F5EFE6]">
              <RefreshCw strokeWidth={1.5} className="h-4 w-4" />
            </button>
            <button onClick={exportCsv} data-testid="admin-export-btn" className="h-9 w-9 rounded-full flex items-center justify-center text-[#8C7A70] hover:bg-[#F5EFE6]">
              <Download strokeWidth={1.5} className="h-4 w-4" />
            </button>
            </>)}
            <button onClick={logout} data-testid="admin-logout-btn" className="h-9 w-9 rounded-full flex items-center justify-center text-[#8C7A70] hover:bg-[#F5EFE6]">
              <LogOut strokeWidth={1.5} className="h-4 w-4" />
            </button>
          </div>
        </div>
        {tab === "leads" && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {chips.map((c) => (
            <button
              key={c.key}
              data-testid={`admin-chip-${c.key}`}
              onClick={() => setFilter(c.key)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === c.key
                  ? "bg-[#1C1613] text-[#FFFFFF]"
                  : "border border-[#E6DDD4] text-[#8C7A70] hover:border-[#8C7A70]"
              }`}
            >
              {c.label} ({c.count})
            </button>
          ))}
        </div>
        )}
      </div>

      {tab === "trafic" ? (
        <TrafficDashboard onUnauthorized={logout} />
      ) : (
      <div className="flex-1 px-6 py-5 space-y-3">
        {status === "loading" && (
          <p className="text-center text-sm text-[#8C7A70] py-10">Chargement…</p>
        )}
        {status === "error" && (
          <div className="text-center py-10">
            <AlertCircle strokeWidth={1.5} className="h-8 w-8 text-[#C62828] mx-auto mb-2" />
            <p className="text-sm text-[#8C7A70]">Erreur de chargement.</p>
            <button onClick={fetchLeads} className="mt-3 text-sm text-[#C84C2C] underline">
              Réessayer
            </button>
          </div>
        )}
        {status === "ok" && filtered.length === 0 && (
          <div className="text-center py-16">
            <Inbox strokeWidth={1.5} className="h-10 w-10 text-[#D8CCBF] mx-auto mb-3" />
            <p className="text-sm text-[#8C7A70]">Aucun lead pour ce filtre.</p>
          </div>
        )}
        {status === "ok" && filtered.map((l) => <LeadCard key={l.id} lead={l} onDelete={deleteLead} />)}
      </div>
      )}
    </div>
  );
}
